import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { translate as t } from '../utils/i18n';

// Get API URL from env or fallback to dynamic local/network IP
const getBaseUrl = () => {
    // Priority 1: Explicit environment variable
    if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;

    // Priority 2: In production, default to same-origin proxy
    if (import.meta.env.PROD) return `${window.location.origin}/api/v1`;

    // Priority 3: Local development fallback
    const hostname = window.location.hostname;
    return `http://${hostname}:3000/api/v1`;
};

const API_URL = getBaseUrl();

export const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // For refresh tokens
});

// Request interceptor for auth - REMOVED (Cookies handled by browser)
// apiClient.interceptors.request.use(...)

// Queue for concurrent 401 requests
let isRefreshing = false;
let refreshQueue: any[] = [];

const processQueue = (error: any) => {
    refreshQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });
    refreshQueue = [];
};

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Log detailed error for debugging on external server
        console.error('[API Error Detail]', {
            url: originalRequest.url,
            method: originalRequest.method,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        });

        // Skip global handling for customer order endpoints to avoid disturbing UX
        const isCustomerRequest =
            originalRequest.url?.includes('/orders/table/') ||
            originalRequest.url?.includes('/orders/active') ||
            originalRequest.url?.includes('/tables/validate') ||
            originalRequest.url?.includes('/menu/') ||
            originalRequest.headers?.['X-QR-Token'];

        // Extract error message from backend if available
        const backendError = error.response?.data as any;
        const errorObject = backendError?.error;
        const errorCode = errorObject?.code;
        const errorMessage = errorObject?.message;

        // Detect MAINTENANCE mode and redirect customer
        const errorMsg = (typeof backendError?.error === 'string' ? backendError.error : errorMessage) || backendError?.message || '';
        const isMaintenance =
            (typeof errorMsg === 'string' && errorMsg.includes('MAINTENANCE')) ||
            (error.response?.status === 403 && errorMsg === 'Table is under maintenance') ||
            (error.response?.status === 400 && typeof errorMsg === 'string' && errorMsg.includes('MAINTENANCE'));

        if (isMaintenance && isCustomerRequest) {
            console.warn('[API] Maintenance mode detected, redirecting...');
            window.location.href = '/maintenance';
            return Promise.reject(error);
        }

        // Global Error Toasts
        if (error.response) {
            const status = error.response.status;

            if (status === 400) {
                if (errorCode === 'INVALID_UUID' || (typeof errorMessage === 'string' && errorMessage.includes('uuid'))) {
                    toast.error(t('errors.validation'));
                } else {
                    toast.error(t('errors.validation'));
                }
            } else if (status === 403) {
                toast.error(t('errors.forbidden'));
            } else if (status === 404 && !isCustomerRequest) {
                toast.error(t('errors.notFound'));
            } else if (status === 409 && !originalRequest.url?.includes('/menu/upload')) {
                // If it's a session conflict, don't show a generic toast, UI will handle it
                const isSessionConflict = errorMessage === 'ACTIVE_SESSION_EXISTS' ||
                    (typeof backendError === 'string' && backendError.includes('ACTIVE_SESSION_EXISTS'));

                // Order claim conflicts carry their own translated message from the calling component
                const isOrderConflict = errorMessage === 'ORDER_ALREADY_CLAIMED' ||
                    errorMessage === 'ORDER_MODIFIED_CONCURRENTLY';

                if (!isSessionConflict && !isOrderConflict) {
                    toast.error(t('errors.duplicateEntry'));
                }
            } else if (status >= 500) {
                toast.error(t('errors.unexpected'));
            }
        } else if (error.request) {
            // Network error
            toast.error(t('errors.networkError'));
        }

        // Handle 401 and token refresh
        const isAuthRequest = originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
            console.warn('[API] 401 Error Intercepted:', originalRequest.url); // DEBUG
            if (isCustomerRequest) {
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    refreshQueue.push({ resolve, reject });
                })
                    .then(() => apiClient(originalRequest))
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Call refresh endpoint
                console.log('[API] Attempting token refresh...'); // DEBUG
                await apiClient.post('/auth/refresh');
                console.log('[API] Token refresh successful'); // DEBUG
                isRefreshing = false;
                processQueue(null);

                // Retry original request
                return apiClient(originalRequest);
            } catch (refreshError: any) {
                console.error('[API] Token refresh failed:', refreshError);
                isRefreshing = false;
                processQueue(refreshError);

                // Refresh token failed, logout user
                try {
                    localStorage.removeItem('india-place-auth');
                } catch (e) { /* ignore */ }

                // Detect if this was a force-logout (session conflict)
                const refreshStatus = refreshError?.response?.status;
                const backendError = refreshError?.response?.data as any;
                const errorMessage = backendError?.error?.message || backendError?.message || '';

                let reason = '';
                if (refreshStatus === 401) {
                    if (errorMessage.includes('terminated') || errorMessage.includes('conflict')) {
                        reason = '?reason=session_conflict';
                    } else if (errorMessage.includes('expired') || errorMessage.includes('inactivity')) {
                        reason = '?reason=session_expired';
                    } else {
                        reason = '?reason=session_conflict';
                    }
                }

                window.location.href = `/login${reason}`;
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);
